import React, { ComponentProps, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { makeStyles } from 'tss-react/mui';
import EnhancedButton from '@components/_old/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import EnhancedInput from '@components/_old/form/EnhancedInput';
import { MAIN_THEME } from '@styles';
import AuthLayout from '@components/layout/AuthLayout';
import { REGEX } from '@utils/validationUtils';
import { useMutation } from '@apollo/client';
import { resetPasswordQuery } from './queries';
import { CDN_URL } from '@utils';

const useStyles = makeStyles()((theme) => ({
  mainContainer: {
    backgroundColor: MAIN_THEME.color.white98,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100vh',
    alignItems: 'center',
    width: '100%',
  },
  logoFig: {
    margin: '0 auto',
    lineHeight: '0',
  },
  boxContainer: {
    maxWidth: '430px',
    width: 'calc(100% - 16px)',
    margin: '46px auto 0',
    boxSizing: 'border-box',
    backgroundColor: MAIN_THEME.color.white,
    border: '1px solid rgba(0,0,0,.125)',
    boxShadow:
      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    borderRadius: '4px',
  },
  greeting: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.xxl,
    lineHeight: '36px',
    textAlign: 'center',
    fontFamily: MAIN_THEME.font.bold,
    letterSpacing: '0px',
    width: '100%',
    margin: '0',
    padding: '28px 0 0 0',
    fontWeight: 'normal',
  },
  submitBtn: {
    margin: '24px 0 12px 0',
    borderRadius: '8px !important',
    fontFamily: `${MAIN_THEME.font.regular} !important`,
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '15px !important',
  },
  formContainer: {
    padding: '24px 30px',
    width: '100%',
  },
  formError: {
    color: theme.palette.error.main,
    height: 'fit-content',
    marginTop: '8px',
    boxSizing: 'border-box',
    padding: '0px 12px 0px 12px',
    fontSize: MAIN_THEME.fontSize.xs,
    textAlign: 'center',
  },
  inputLabel: {
    fontFamily: `${MAIN_THEME.font.regular} !important`,
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  backToSignIn: {
    color: MAIN_THEME.color.black,
    border: 0,
    cursor: 'pointer',
    background: 'none',
    textDecoration: 'underline',
    marginTop: '66px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

type ForgotPasswordFormProps = {
  onBackToSignInClicked: () => void;
};

type ForgotPasswordForm = {
  email: string;
};

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBackToSignInClicked,
}) => {
  const { classes } = useStyles();
  const [resetPasswordAction] = useMutation(resetPasswordQuery);

  const form = useForm<ForgotPasswordForm>({
    defaultValues: {
      email: '',
    },
  });

  const { handleSubmit, formState } = form;

  const [btnState, setBtnState] =
    useState<ComponentProps<typeof EnhancedButton>['state']>();

  const onSubmit = async (formValues: ForgotPasswordForm) => {
    setBtnState('loading');
    resetPasswordAction({
      variables: {
        email: formValues.email,
      },
    })
      .then((response) => {
        if (response.data) {
          setBtnState('success');
          toast.success(
            <ToastSuccessMessage>
              An email has been sent to your inbox!
            </ToastSuccessMessage>
          );
          setTimeout(() => {
            setBtnState(undefined);
            // navigate(`/login${window.location.search}`);
          }, 500);
        } else if (response.errors) {
          setBtnState(undefined);
          toast.error(
            <ToastErrorMessage>
              Access Denied. Please contact system administrator
            </ToastErrorMessage>
          );
          // navigate(`/login${window.location.search}`);
        }
      })
      .catch(() => {
        setBtnState(undefined);
        toast.error(
          <ToastErrorMessage>
            Access Denied. Please contact system administrator
          </ToastErrorMessage>
        );
      });
  };

  return (
    <AuthLayout>
      <figure className={classes.logoFig}>
        <img
          height={117}
          width={125}
          src={CDN_URL + '/images/logo-large-red.png'}
          alt={'UFA logo'}
        />
      </figure>
      <div className={classes.boxContainer}>
        <h1 className={classes.greeting}>Forgot Password</h1>
        <div className={classes.formContainer}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormProvider {...form}>
              <Controller
                name={'email'}
                control={form.control}
                rules={{
                  required: 'Required',
                  pattern: {
                    value: REGEX.email,
                    message: 'Invalid format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <EnhancedInput
                    key={field.name}
                    title={'Email'}
                    type="text"
                    placeholder="john@example.com"
                    error={fieldState.error?.message || ''}
                    disabled={formState.isSubmitting || formState.isLoading}
                    customStyles={{
                      labelStyles: classes.inputLabel,
                    }}
                    {...field}
                  />
                )}
              />
              <div className={classes.footerContainer}>
                <EnhancedButton
                  type="submit"
                  disabled={formState.isSubmitting || formState.isLoading}
                  variant="contained"
                  backgroundColor={MAIN_THEME.color.black}
                  color="rgba(255, 255, 255, 1)"
                  style={{ width: '108px' }}
                  state={btnState}
                  className={classes.submitBtn}
                >
                  SUBMIT
                </EnhancedButton>
              </div>
            </FormProvider>
          </form>
          <button
            className={classes.backToSignIn}
            onClick={onBackToSignInClicked}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPasswordForm;
